import { useState } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, PackageCheck, Handshake, Megaphone, Headphones } from "lucide-react";
import { submitDealerEnquiry } from "../lib/api";

import SEO from "../components/SEO";
const PERKS = [
  { Icon: PackageCheck, title: "Growing Range", text: "Rubber, cotton and industrial coated gloves" },
  { Icon: Handshake, title: "B2B Focus", text: "Built for dealers, distributors and industrial buyers" },
  { Icon: Megaphone, title: "Marketing Support", text: "Product creatives and catalogue support" },
  { Icon: Headphones, title: "Direct Coordination", text: "Clear communication for enquiries and orders" },
];

export default function DealerEnquiry() {
  const [form, setForm] = useState({ name: "", company: "", mobile: "", email: "", location: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.mobile) {
      toast.error("Name and mobile are required");
      return;
    }
    setLoading(true);
    try {
      await submitDealerEnquiry(form);
      toast.success("Application received. Our team will contact you.");
      setForm({ name: "", company: "", mobile: "", email: "", location: "", message: "" });
    } catch (error) {
      toast.error("Could not submit. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Become an RSN Gloves Dealer"
        description="Apply to become an RSN Gloves dealer or distribution partner for rubber, cotton and industrial coated hand gloves."
        path="/dealer-enquiry"
      />
    <div data-testid="dealer-page" className="bg-[#FBFCFA] min-h-screen">
      <section className="bg-white border-b border-[#DFE7E2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <div className="section-kicker">Dealer & Distributor Program</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0F172A] mt-2">Grow with RSN Gloves.</h1>
          <p className="mt-3 max-w-2xl text-[#5F6B66]">Tell us about your business and market. We will connect with you for relevant distribution opportunities.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2">
          <div className="grid sm:grid-cols-2 gap-4">
            {PERKS.map(({ Icon, title, text }) => (
              <div key={title} className="soft-card p-5">
                <Icon className="w-6 h-6 text-[#075B3A]" strokeWidth={1.6} />
                <div className="font-display text-lg font-black text-[#0F172A] mt-4">{title}</div>
                <div className="text-sm text-[#5F6B66] mt-1">{text}</div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-3 soft-card p-6 sm:p-8 space-y-5" data-testid="dealer-form">
          <h2 className="font-display text-2xl font-black text-[#0F172A]">Dealer / Distributor Application</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *"><Input value={form.name} onChange={update("name")} className="mt-1.5 h-11" required /></Field>
            <Field label="Company / Firm"><Input value={form.company} onChange={update("company")} className="mt-1.5 h-11" /></Field>
            <Field label="Mobile *"><Input value={form.mobile} onChange={update("mobile")} className="mt-1.5 h-11" required /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={update("email")} className="mt-1.5 h-11" /></Field>
            <div className="sm:col-span-2"><Field label="City / State"><Input value={form.location} onChange={update("location")} className="mt-1.5 h-11" placeholder="e.g. Nagpur, Maharashtra" /></Field></div>
            <div className="sm:col-span-2"><Field label="Message"><Textarea rows={4} value={form.message} onChange={update("message")} className="mt-1.5" placeholder="Tell us about your business and current product lines..." /></Field></div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#075B3A] hover:bg-[#04412B] text-white font-bold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Application"}
          </Button>
        </form>
      </section>
    </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-[0.14em] text-[#65736B]">{label}</Label>
      {children}
    </div>
  );
}
